import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';

// Sharp Material Design Icons as SVG components
const DatabaseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3C7.58 3 4 4.79 4 7s3.58 4 8 4 8-1.79 8-4-3.58-4-8-4zM4 9v3c0 2.21 3.58 4 8 4s8-1.79 8-4V9c0 2.21-3.58 4-8 4s-8-1.79-8-4zm0 5v3c0 2.21 3.58 4 8 4s8-1.79 8-4v-3c0 2.21-3.58 4-8 4s-8-1.79-8-4z"/>
  </svg>
);

const CloudIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
  </svg>
);

const WorkflowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H4v20h16V8l-6-6zm4 18H6V4h7v5h5v11z"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H4v20h16V8l-6-6zm4 18H6V4h7v5h5v11z"/>
  </svg>
);

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.1 16,12.7V16.2C16,16.8 15.4,17.5 14.8,17.5H9.2C8.6,17.5 8,16.8 8,16.2V12.8C8,12.2 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"/>
  </svg>
);

interface Connection {
  id: string;
  fromIndex: number;
  toIndex: number;
  startTime: number;
}

export const DocumentSecurityAnimation: React.FC = () => {
  const [connections, setConnections] = useState<Connection[]>([]);

  // Memoize constants to prevent recalculations
  const { nodePositions, nodeIcons, logoPosition } = useMemo(() => {
    const center = { x: 300, y: 200 };
    const radius = 120;
    
    return {
      CENTER: center,
      nodePositions: Array.from({ length: 6 }, (_, i) => {
        const angle = (i * Math.PI * 2) / 6;
        return {
          x: center.x + Math.cos(angle) * radius,
          y: center.y + Math.sin(angle) * radius
        };
      }),
      nodeIcons: [DatabaseIcon, CloudIcon, WorkflowIcon, FileTextIcon, LockIcon, ShieldIcon],
      logoPosition: center
    };
  }, []);

  // Memoize connection paths to avoid recalculation
  const connectionPaths = useMemo(() => {
    const radius = 120;
    return Array.from({ length: 6 }, (_, i) => {
      const fromIndex = i;
      const toIndex = (i + 1) % 6;
      const from = nodePositions[fromIndex];
      const to = nodePositions[toIndex];
      
      return `M ${from.x} ${from.y} A ${radius} ${radius} 0 0 1 ${to.x} ${to.y}`;
    });
  }, [nodePositions]);

  // Optimized connection creation
  const createConnection = useCallback((connectionIndex: number) => {
    const fromIndex = connectionIndex;
    const toIndex = (connectionIndex + 1) % 6;
    
    return {
      id: `${fromIndex}-${toIndex}-${Date.now()}`,
      fromIndex,
      toIndex,
      startTime: Date.now(),
    };
  }, []);

  useEffect(() => {
    let connectionCounter = 0;
    let mounted = true;
    
    const addConnection = () => {
      if (!mounted) return;
      
      const connection = createConnection(connectionCounter % 6);
      setConnections(prev => [...prev, connection]);
      connectionCounter++;
    };

    // Separate intervals for creation and cleanup to maintain circular flow
    const createInterval = setInterval(addConnection, 300);
    
    const cleanupInterval = setInterval(() => {
      if (!mounted) return;
      const now = Date.now();
      setConnections(prev => prev.filter(conn => now - conn.startTime < 1000));
    }, 100);

    // Initial connection
    addConnection();

    return () => {
      mounted = false;
      clearInterval(createInterval);
      clearInterval(cleanupInterval);
    };
  }, [createConnection]);

  return (
    <div className="w-full h-full min-h-[400px] flex items-center justify-center">
      <svg
        viewBox="-20 -20 640 440"
        className="w-full h-full max-w-[600px] max-h-[400px]"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4040FF" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0000bd" stopOpacity="0.3" />
          </linearGradient>
          <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4040FF" />
            <stop offset="100%" stopColor="#0000bd" />
          </radialGradient>
        </defs>

        {/* Render connections */}
        {connections.map((connection) => (
          <motion.path
            key={connection.id}
            d={connectionPaths[connection.fromIndex]}
            stroke="#0000bd"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0, opacity: 1 }}
            animate={{ pathLength: 1, opacity: [1, 1, 0] }}
            transition={{ 
              pathLength: { duration: 0.25, ease: "easeOut" },
              opacity: { duration: 1.0, times: [0, 0.5, 1] }
            }}
          />
        ))}

        {/* LogOmark at center */}
        <image
          href="/LOGOmark.png"
          x={logoPosition.x - 36}
          y={logoPosition.y - 36}
          width="72"
          height="72"
        />

        {/* Render nodes */}
        {nodePositions.map((position, index) => {
          const IconComponent = nodeIcons[index];
          return (
            <g key={index}>
              {/* Node circle */}
              <circle
                cx={position.x}
                cy={position.y}
                r="24"
                fill="url(#nodeGradient)"
              />
              
              {/* Pulse ring */}
              <motion.circle
                cx={position.x}
                cy={position.y}
                r={24}
                fill="none"
                stroke="#4040FF"
                strokeWidth="2"
                strokeOpacity="0.6"
                animate={{
                  r: [24, 38],
                  strokeOpacity: [0.6, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
              />
              
              {/* Icon */}
              <foreignObject
                x={position.x - 12}
                y={position.y - 12}
                width="24"
                height="24"
              >
                <div className="flex items-center justify-center w-full h-full text-white">
                  <IconComponent />
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

 